import { mediaUrl } from '../api.js';

/** Escape HTML */
export function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function $(sel, root = document) {
  return root.querySelector(sel);
}

export function $$(sel, root = document) {
  return Array.from(root.querySelectorAll(sel));
}

export function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

export function formatTime(input) {
  if (!input) return '';
  // SQLite datetime often lacks timezone — treat as UTC if no Z/offset
  let s = String(input);
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(s) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(s)) {
    s = s.replace(' ', 'T') + 'Z';
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(input);
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return '刚刚';
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} 天前`;
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function avatarUrl(user) {
  const raw = user?.avatar_url || user?.avatar;
  if (raw) return mediaUrl(raw);
  const name = encodeURIComponent(user?.nickname || user?.gid || '?');
  return `https://ui-avatars.com/api/?name=${name}&background=1e3a8a&color=fff&size=128&bold=true`;
}

export function toast(message, type = 'info') {
  let host = document.getElementById('toast-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'toast-host';
    host.className = 'toast-host';
    document.body.appendChild(host);
  }
  const node = document.createElement('div');
  node.className = `toast toast-${type}`;
  node.textContent = message;
  host.appendChild(node);
  requestAnimationFrame(() => node.classList.add('show'));
  setTimeout(() => {
    node.classList.remove('show');
    setTimeout(() => node.remove(), 300);
  }, 2800);
}

export function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  ta.remove();
  return Promise.resolve();
}

export function postShareUrl(postId) {
  return `${location.origin}${location.pathname}#/post/${postId}`;
}

export function profileShareUrl(gid) {
  return `${location.origin}${location.pathname}#/user/${gid}`;
}

export function isVerified(user) {
  return !!(user?.is_verified || user?.verified || user?.blueV);
}

export const BADGE_TITLES = {
  student:
    '学生标识（绿点）：通过学生邀请码注册后自动显示。',
  blue:
    '蓝 V：在本平台有 ≥1500 粉丝，且至少 50 篇原创内容。由管理员审核开通。',
  red:
    '红 V（认证老师）：至少 30 篇原创内容，且能证明自己是老师或已加入 GSEDU。可通过老师邀请码（单次有效）注册获得，或联系管理员。',
  gold:
    '金 V（商用）：须为 ≥2 人的组织（不要求工商登记），本平台粉丝 ≥500，且须有官网；无官网可用 cloud.tanstudio.me。联系管理员开通。',
};

export const ADMIN_QQ = '3251989534';

/**
 * Render verification badges next to a display name.
 * Green student dot + thick blue / red / gold V marks with hover titles.
 */
export function badgeHtml(user) {
  if (!user) return '';
  const parts = [];
  if (user.is_student) {
    parts.push(
      `<span class="badge badge-student" title="${esc(BADGE_TITLES.student)}" aria-label="学生"></span>`
    );
  }
  if (user.is_verified) {
    parts.push(
      `<span class="badge badge-blue" title="${esc(BADGE_TITLES.blue)}" aria-label="蓝V">✓</span>`
    );
  }
  if (user.is_teacher) {
    parts.push(
      `<span class="badge badge-red" title="${esc(BADGE_TITLES.red)}" aria-label="红V">✓</span>`
    );
  }
  if (user.is_gold) {
    parts.push(
      `<span class="badge badge-gold" title="${esc(BADGE_TITLES.gold)}" aria-label="金V">✓</span>`
    );
  }
  return parts.join('');
}

export function openHelpModal(cfg = {}) {
  document.getElementById('help-modal')?.remove();
  const qq = cfg.adminQQ || cfg.badgeHelp?.admin_qq || ADMIN_QQ;
  const help = cfg.badgeHelp || {};
  const row = (key, fallbackName, fallbackDesc) => {
    const h = help[key] || {};
    return `<div class="help-item">
      <div class="help-item-title">${esc(h.name || fallbackName)}</div>
      <div class="help-item-desc">${esc(h.desc || fallbackDesc)}</div>
    </div>`;
  };
  const backdrop = document.createElement('div');
  backdrop.id = 'help-modal';
  backdrop.className = 'modal-backdrop';
  backdrop.innerHTML = `
    <div class="modal modal-wide">
      <div class="modal-header">
        <h3>认证与帮助</h3>
        <button type="button" class="btn btn-ghost btn-sm" data-close>✕</button>
      </div>
      <div class="modal-body help-body">
        <div class="help-qq">管理员 QQ：<strong>${esc(qq)}</strong>（邀请码 / 认证 / 申诉）</div>
        ${row('invite', '注册邀请码', `注册必须填写有效邀请码。无邀请码请联系管理员 QQ：${qq}`)}
        ${row('green', '学生标识（绿点）', BADGE_TITLES.student)}
        ${row('blue', '蓝 V', BADGE_TITLES.blue)}
        ${row('red', '红 V（认证老师）', BADGE_TITLES.red)}
        ${row('gold', '金 V（商用）', BADGE_TITLES.gold)}
        ${row('risk', '风控说明', `关注：每10分钟最多15人、每天最多30人；单日被关注>100将限制关注至次日。1分钟发帖>20将限制发帖1小时。单日敏感词>10次封禁10天。申诉 QQ：${qq}`)}
        <div class="help-item">
          <div class="help-item-title">搭建官网</div>
          <div class="help-item-desc">若尚有官网，可使用 <a href="https://cloud.tanstudio.me" target="_blank" rel="noopener">cloud.tanstudio.me</a> 搭建。</div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  backdrop.querySelector('[data-close]')?.addEventListener('click', close);
}
